import { useCallback, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import type { InspectionTemplate } from 'inspection-types';
import {
  useForkInspectionTemplate,
  useHideInspectionTemplate,
  useUnhideInspectionTemplate,
} from 'react-hooks';
import { SourceType } from 'graphql-query-contracts';

import { isTemplateOwnedByChannel } from '../helpers/isTemplateOwnedByChannel';
import type { InspectionsChannel } from '../types/channel';

export type VisibilityChangedResult = {
  /** The template that was actually hidden/unhidden — the FORKED id when a fork occurred. */
  templateId: string;
  hidden: boolean;
  forkedFromTemplateId?: string;
};

type UseToggleInspectionTemplateVisibilityArgs = {
  channel: InspectionsChannel;
  /**
   * Called once the mutation has succeeded, with the definitive outcome.
   * Callers should apply this directly to the affected row rather than
   * relying solely on the list refetch that follows — the `inspectionTemplates`
   * read model can lag the write for a beat, so a refetch immediately after
   * hiding/unhiding may still report the pre-change value. This callback
   * still triggers that refetch (a fork mints a NEW template id, so the row
   * must be replaced) and is awaited before the loading modal is dismissed,
   * but the caller's own optimistic state is what should drive the UI.
   */
  onVisibilityChanged: (result: VisibilityChangedResult) => Promise<void>;
};

type UseToggleInspectionTemplateVisibilityResult = {
  toggleTemplateVisibility: (template: InspectionTemplate) => Promise<void>;
};

/**
 * Orchestrates hiding/unhiding a template from the Template Library.
 *
 * Flow:
 *  - Changing visibility requires the channel to own the template (the
 *    mutations validate building ownership server-side). If it isn't owned
 *    yet, this forks a building-owned copy first, then hides/unhides that
 *    copy. This applies to both directions: unhiding a foreign (parent)
 *    template directly would make it visible for every channel using it, so
 *    the fork keeps the change local. In the common case a hidden template is
 *    already an owned fork, so unhide won't re-fork it.
 *  - Both directions are confirmed first, with direction-specific copy —
 *    hiding removes the template from every mobile inspector at this
 *    property, and making it visible puts it back in front of them.
 *  - On success, the list is refetched and awaited before the loading modal
 *    closes, so the row's new state is already on screen — no page refresh
 *    needed. On error, the loading modal is dismissed and a toast shown
 *    without refetching.
 *  - Concurrent calls are de-duped via an in-flight guard.
 */
export function useToggleInspectionTemplateVisibility({
  channel,
  onVisibilityChanged,
}: UseToggleInspectionTemplateVisibilityArgs): UseToggleInspectionTemplateVisibilityResult {
  const { t } = useTranslation();
  const { forkInspectionTemplate } = useForkInspectionTemplate();
  const { hideInspectionTemplate } = useHideInspectionTemplate();
  const { unhideInspectionTemplate } = useUnhideInspectionTemplate();
  const inFlightRef = useRef(false);

  const toggleTemplateVisibility = useCallback(
    async (template: InspectionTemplate) => {
      if (inFlightRef.current) {
        return;
      }

      // Set before the (async) confirm dialog too, so a second click while
      // the dialog is still open doesn't stack a duplicate confirm.
      inFlightRef.current = true;

      const nextHidden = !template.hidden;

      // Both directions confirm first: hiding removes the template from every
      // mobile inspector at this property, and making it visible puts it back
      // in front of them.
      try {
        await window.Alert.confirm({
          title: t(
            nextHidden
              ? 'web.admin.inspections.templateLibrary.visibility.hideConfirmTitle'
              : 'web.admin.inspections.templateLibrary.visibility.unhideConfirmTitle'
          ),
          message: t(
            nextHidden
              ? 'web.admin.inspections.templateLibrary.visibility.hideConfirmMessage'
              : 'web.admin.inspections.templateLibrary.visibility.unhideConfirmMessage'
          ),
          confirmText: t(
            nextHidden
              ? 'web.admin.inspections.templateLibrary.visibility.hideConfirmButton'
              : 'web.admin.inspections.templateLibrary.visibility.unhideConfirmButton'
          ),
          cancelText: t(
            'web.admin.inspections.templateLibrary.visibility.cancelButton'
          ),
        });
      } catch {
        // Alert.confirm rejects when the user cancels — nothing to do.
        inFlightRef.current = false;

        return;
      }

      try {
        window.Alert.loading({
          title: t('web.admin.inspections.templateLibrary.visibility.preparing'),
          disableCloseOnBackgroundClick: true,
        });

        let targetTemplateId = template.id;
        let forkedFromTemplateId: string | undefined;

        if (!isTemplateOwnedByChannel(template, channel)) {
          const buildingEntityType = 'building';

          const { templateId } = await forkInspectionTemplate({
            parentTemplateId: template.id,
            buildingId: {
              entityId: channel._id,
              entityType: buildingEntityType,
              sourceType: SourceType.SourceTypeActivate,
            },
          });

          forkedFromTemplateId = template.id;
          targetTemplateId = templateId;
        }

        if (nextHidden) {
          await hideInspectionTemplate({
            channelId: channel._id,
            templateId: targetTemplateId,
          });
        } else {
          await unhideInspectionTemplate({
            channelId: channel._id,
            templateId: targetTemplateId,
          });
        }

        // The mutation succeeding is the source of truth for this row — pass
        // it on directly rather than waiting to see what the list refetch
        // reports, since that read model can lag the write. Still await the
        // refetch itself before dismissing the loading modal.
        await onVisibilityChanged({ templateId: targetTemplateId, hidden: nextHidden, forkedFromTemplateId });

        window.Alert.hide();
        await window.Toast.show(
          t(
            nextHidden
              ? 'web.admin.inspections.templateLibrary.visibility.hideSuccessToast'
              : 'web.admin.inspections.templateLibrary.visibility.unhideSuccessToast'
          )
        );
      } catch {
        window.Alert.hide();
        await window.Toast.show(
          t(
            nextHidden
              ? 'web.admin.inspections.templateLibrary.visibility.hideErrorToast'
              : 'web.admin.inspections.templateLibrary.visibility.unhideErrorToast'
          )
        );
      } finally {
        inFlightRef.current = false;
      }
    },
    [
      channel,
      forkInspectionTemplate,
      hideInspectionTemplate,
      onVisibilityChanged,
      t,
      unhideInspectionTemplate,
    ]
  );

  return { toggleTemplateVisibility };
}
